package com.evoapps.evoshift.domain.forecast

import kotlinx.datetime.DatePeriod
import kotlinx.datetime.DayOfWeek
import kotlinx.datetime.LocalDate
import kotlinx.datetime.daysUntil
import kotlinx.datetime.minus
import kotlinx.datetime.plus
import kotlin.math.abs
import kotlin.math.roundToInt

/*
 * SH-120: a sales forecast from the workplace's own history (its daily records' net takings), no
 * outside data. Rules a manager can redo by hand, so every number on the screen can be explained:
 *
 *   1. Enough history: takings over at least [SalesForecaster.MIN_HISTORY_WEEKS] weeks (and on
 *      [SalesForecaster.MIN_DAYS_WITH_SALES] days). Less: no forecast at all.
 *   2. Weekday seasonality: the average takings of the last [SalesForecaster.SAME_DAYS] same
 *      weekdays (public holidays left out), at least [SalesForecaster.MIN_SAME_DAYS] of them.
 *      A trading Sunday is a Sunday like the others the workplace was open on.
 *   3. Recent trend: per weekday, the last 4 weeks' average against the 4 weeks before; the mean of
 *      those ratios, damped by half (the same-weekday average already lags a few weeks; damping
 *      keeps one good week from running away) and capped at ±[SalesForecaster.MAX_TREND_PERCENT]%.
 *      Rounded to a whole percent, so "+4% trend" is exactly what was applied.
 *   4. Public holidays: a holiday the workplace is open on moves by how its past holidays did
 *      against their usual weekday (a whole percent); with no past holiday, no forecast that day.
 *   5. The amount is rounded to [SalesForecaster.ROUND_TO].
 *
 * The domain returns the facts ([ForecastBasis]); the screens word them.
 */

/** One day of history: the net takings and the hours actually worked (0 when unknown). */
data class SalesHistoryDay(
    val date: LocalDate,
    val netSales: Double,
    val labourHours: Double = 0.0
)

enum class ForecastStatus {
    READY,
    /** Less than [SalesForecaster.MIN_HISTORY_WEEKS] weeks of takings: nothing is suggested. */
    TOO_LITTLE_HISTORY
}

enum class ForecastBasisKind {
    /** The average of the last same weekdays. */
    WEEKDAY,
    /** A trading Sunday: the average of the last Sundays the workplace was open on. */
    TRADING_SUNDAY,
    /** A public holiday: the weekday's average moved by how past holidays did. */
    HOLIDAY
}

/** Why a day's forecast is what it is: "avg of the last 6 Fridays, +4% trend". */
data class ForecastBasis(
    val kind: ForecastBasisKind,
    val dayOfWeek: DayOfWeek,
    /** How many same weekdays (or open Sundays) the average is of. */
    val sameDays: Int,
    /** Their average net takings. */
    val average: Double,
    /** The trend applied, whole percent (+4 = 4% up). */
    val trendPercent: Int,
    /** [ForecastBasisKind.HOLIDAY]: the past holidays' change against their weekday, whole percent. */
    val holidayPercent: Int? = null,
    /** [ForecastBasisKind.HOLIDAY]: how many past holidays that is of. */
    val pastHolidays: Int = 0
)

/** One day's suggested net sales target. */
data class DayForecast(
    val date: LocalDate,
    val amount: Double,
    val basis: ForecastBasis
)

/** The forecast of some days, or [ForecastStatus.TOO_LITTLE_HISTORY] with none. */
data class SalesForecast(
    val status: ForecastStatus,
    val days: List<DayForecast>,
    /** The trend applied to every day, whole percent. */
    val trendPercent: Int = 0,
    /** Days with takings in the history used. */
    val historyDays: Int = 0,
    /** Whole weeks the history covers. */
    val historyWeeks: Int = 0
) {
    val byDate: Map<LocalDate, DayForecast> by lazy { days.associateBy { it.date } }

    companion object {
        fun tooLittle(historyDays: Int, historyWeeks: Int) =
            SalesForecast(ForecastStatus.TOO_LITTLE_HISTORY, emptyList(), historyDays = historyDays, historyWeeks = historyWeeks)
    }
}

object SalesForecaster {
    const val MIN_HISTORY_WEEKS = 8
    const val MIN_DAYS_WITH_SALES = 28
    /** How many past same weekdays the average looks at. */
    const val SAME_DAYS = 6
    /** Fewer than this many same weekdays: no forecast for that weekday. */
    const val MIN_SAME_DAYS = 3
    const val TREND_WINDOW_DAYS = 28
    const val TREND_DAMPING = 0.5
    const val MAX_TREND_PERCENT = 10
    /** Days with takings needed in each trend window, else no trend (0%). */
    const val MIN_TREND_DAYS = 8
    /** Same weekdays around a past holiday that make its usual level. */
    const val MIN_HOLIDAY_BASE_DAYS = 2
    const val ROUND_TO = 10.0

    /**
     * The forecast of [days] from [history] (days without takings, or with none, are left out).
     * Only history before the first day forecast is used, so a day never explains itself.
     * [holidays]: the public holidays of the years involved; [tradingSundays]: the year's trading
     * Sundays (only the wording of a Sunday's basis depends on it).
     */
    fun forecast(
        history: List<SalesHistoryDay>,
        days: List<LocalDate>,
        holidays: Set<LocalDate>,
        tradingSundays: Set<LocalDate> = emptySet()
    ): SalesForecast {
        val firstDay = days.minOrNull()
        val past = history
            .filter { it.netSales > 0 && (firstDay == null || it.date < firstDay) }
            .distinctBy { it.date }
            .sortedBy { it.date }
        if (past.isEmpty()) return SalesForecast.tooLittle(0, 0)
        // The history ends where the takings end (days not entered yet are not "no sales").
        val anchor = past.last().date.plus(DatePeriod(days = 1))
        // Whole weeks from the first takings to the last; a closed last day of the week (a Sunday)
        // does not cost a week: Monday 12 Jan to Saturday 7 Mar is 8 weeks.
        val weeks = (past.first().date.daysUntil(anchor) + 1) / 7
        if (weeks < MIN_HISTORY_WEEKS || past.size < MIN_DAYS_WITH_SALES) return SalesForecast.tooLittle(past.size, weeks)

        val regular = past.filter { it.date !in holidays }
        val trend = trendPercent(regular, anchor)
        val forecasts = days.sorted().distinct().mapNotNull { date ->
            if (date in holidays) holidayForecast(date, regular, past, holidays, trend)
            else weekdayForecast(date, regular, trend, tradingSundays)
        }
        return SalesForecast(ForecastStatus.READY, forecasts, trend, past.size, weeks)
    }

    /** The last [SAME_DAYS] regular days of [day]'s weekday before [before], newest first. */
    private fun sameWeekdays(regular: List<SalesHistoryDay>, day: DayOfWeek, before: LocalDate? = null): List<SalesHistoryDay> =
        regular.asReversed().filter { it.date.dayOfWeek == day && (before == null || it.date < before) }.take(SAME_DAYS)

    private fun weekdayForecast(date: LocalDate, regular: List<SalesHistoryDay>, trend: Int, tradingSundays: Set<LocalDate>): DayForecast? {
        val same = sameWeekdays(regular, date.dayOfWeek)
        if (same.size < MIN_SAME_DAYS) return null
        val average = same.map { it.netSales }.average()
        val kind = if (date.dayOfWeek == DayOfWeek.SUNDAY && date in tradingSundays) ForecastBasisKind.TRADING_SUNDAY else ForecastBasisKind.WEEKDAY
        return DayForecast(
            date = date,
            amount = round(average * (1 + trend / 100.0)),
            basis = ForecastBasis(kind, date.dayOfWeek, same.size, average, trend)
        )
    }

    private fun holidayForecast(
        date: LocalDate,
        regular: List<SalesHistoryDay>,
        past: List<SalesHistoryDay>,
        holidays: Set<LocalDate>,
        trend: Int
    ): DayForecast? {
        val same = sameWeekdays(regular, date.dayOfWeek)
        if (same.size < MIN_SAME_DAYS) return null
        // Each past open holiday against the same weekdays around it (its usual level then).
        val ratios = past.filter { it.date in holidays }.mapNotNull { holiday ->
            val around = regular
                .filter { it.date.dayOfWeek == holiday.date.dayOfWeek && abs(it.date.daysUntil(holiday.date)) <= 7 * SAME_DAYS }
                .sortedBy { abs(it.date.daysUntil(holiday.date)) }
                .take(SAME_DAYS)
            if (around.size < MIN_HOLIDAY_BASE_DAYS) null else holiday.netSales / around.map { it.netSales }.average()
        }
        if (ratios.isEmpty()) return null
        val holidayPercent = ((ratios.average() - 1) * 100).roundToInt()
        val average = same.map { it.netSales }.average()
        return DayForecast(
            date = date,
            amount = round(average * (1 + trend / 100.0) * (1 + holidayPercent / 100.0)),
            basis = ForecastBasis(ForecastBasisKind.HOLIDAY, date.dayOfWeek, same.size, average, trend, holidayPercent, ratios.size)
        )
    }

    /**
     * The trend in whole percent: per weekday, the average of the last [TREND_WINDOW_DAYS] days
     * before [anchor] against the same window before that; the mean ratio, damped and capped.
     * Sundays are left out (trading Sundays come a few times a year and would swing it).
     */
    internal fun trendPercent(regular: List<SalesHistoryDay>, anchor: LocalDate): Int {
        val recentStart = anchor.minus(DatePeriod(days = TREND_WINDOW_DAYS))
        val priorStart = recentStart.minus(DatePeriod(days = TREND_WINDOW_DAYS))
        val usable = regular.filter { it.date.dayOfWeek != DayOfWeek.SUNDAY }
        val recent = usable.filter { it.date >= recentStart && it.date < anchor }
        val prior = usable.filter { it.date >= priorStart && it.date < recentStart }
        if (recent.size < MIN_TREND_DAYS || prior.size < MIN_TREND_DAYS) return 0
        val recentByDay = recent.groupBy { it.date.dayOfWeek }
        val priorByDay = prior.groupBy { it.date.dayOfWeek }
        val ratios = recentByDay.keys.intersect(priorByDay.keys).map { day ->
            recentByDay.getValue(day).map { it.netSales }.average() / priorByDay.getValue(day).map { it.netSales }.average()
        }
        if (ratios.isEmpty()) return 0
        val raw = (ratios.average() - 1) * 100 * TREND_DAMPING
        return raw.roundToInt().coerceIn(-MAX_TREND_PERCENT, MAX_TREND_PERCENT)
    }

    private fun round(amount: Double): Double = (amount / ROUND_TO).roundToInt() * ROUND_TO
}
